import { useCallback, useRef, type ReactNode } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	formControlHeight,
	formPrimary,
	getSelectChevron,
	selectChevronCapClassName,
	selectChevronCapDisabledClassName,
	selectChevronDisabledIconColor,
} from "@config/theme/formControls";

interface SelectFieldWrapperProps {
	children: ReactNode;
	className?: string;
	disabled?: boolean;
	/** Match MUI select height so the chevron cap scales (48 default, 40 for Year). */
	controlHeight?: number;
}

/** Positions the shared chevron button and opens the nested MUI Select on click. */
export const SelectFieldWrapper = ({
	children,
	className = "",
	disabled = false,
	controlHeight = formControlHeight,
}: SelectFieldWrapperProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const chevron = getSelectChevron(controlHeight);
	const iconSize = controlHeight <= 44 ? 14 : 16;

	const openSelect = useCallback(() => {
		if (disabled) return;

		const root = wrapperRef.current;
		if (!root) return;

		const combo = root.querySelector('[role="combobox"]') as HTMLElement | null;
		const selectArea = root.querySelector(".MuiSelect-select") as HTMLElement | null;
		const target = combo ?? selectArea;
		if (!target) return;

		target.dispatchEvent(
			new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }),
		);
		target.click();
	}, [disabled]);

	return (
		<div ref={wrapperRef} className={`relative w-full [&_.MuiInputBase-root]:!shadow-none ${className}`}>
			{children}
			<button
				type="button"
				className={disabled ? selectChevronCapDisabledClassName : selectChevronCapClassName}
				style={{
					top: controlHeight / 2,
					transform: "translateY(-50%)",
					width: chevron.width,
					height: chevron.height,
					borderRadius: chevron.borderRadius,
					right: chevron.rightInset,
					backgroundColor: disabled ? undefined : formPrimary,
				}}
				onClick={openSelect}
				onMouseDown={e => e.preventDefault()}
				aria-label="Open dropdown"
				aria-disabled={disabled || undefined}
				disabled={disabled}
				tabIndex={-1}
			>
				<KeyboardArrowDownIcon
					sx={{
						color: disabled ? selectChevronDisabledIconColor : "#fff",
						fontSize: iconSize,
						pointerEvents: "none",
					}}
				/>
			</button>
		</div>
	);
};
